package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.subscriptions.onClick
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.installBarPlatform
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.asList
import kotlin.js.Date

/**
 * The behaviour behind the app-install offers rendered by `AppInstall.kt` in `server-pages`.
 *
 * The server ships the markup for both surfaces; this decides what a given device actually sees.
 * Everything it decides on depends on the user agent or on stored state, neither of which the
 * server can see without making every page a separate CDN cache entry.
 *
 * This replaced a full-screen modal that was thrown over the page on arrival. The reasoning is in
 * `specs/app-install-prompts.md`; the two behaviours worth knowing here are that **iOS Safari is
 * never shown the bar**, because the native Smart App Banner already offers the app there, and
 * that a dismissal is now actually recorded — the modal only wrote its timestamp when somebody
 * used one specific link, so in practice it came back on nearly every page view.
 */
fun appInstall(element: Element) {
    when (element.getAttribute("data-install-surface")) {
        "bar" -> installBar(element)
        "footer" -> installFooter(element)
    }
}

/**
 * The footer badges: always visible, both platforms, no suppression.
 *
 * Nothing to decide here — this only attaches tap reporting, so that a footer install and a bar
 * install can be told apart in the funnel as well as in the campaign tags on the link itself.
 */
private fun installFooter(element: Element) {
    reportTapsFrom(element, surface = "footer")
}

/**
 * The bar: shown on a phone, unless this is iOS Safari or the offer was recently dismissed.
 */
private fun installBar(element: Element) {
    val platform = barPlatform() ?: return
    if (isDismissed()) return

    element.setAttribute("data-install-visible", "1")

    // Only the badge for this device. The other is left in the document and hidden, which is
    // cheaper than rendering per user agent and keeps the markup cacheable.
    element.querySelectorAll("a[data-install-platform=\"$platform\"]").asList().forEach {
        (it as? Element)?.setAttribute("data-install-shown", "1")
    }

    // Nothing here reserves room for the bar. The footer does it in CSS, statically, which is the
    // only version that is reliable: measuring the bar at this point reports its height *before*
    // the store badge image has loaded, so the reservation came out ~17px short and let the bar
    // cover the end of the footer. A layout constant belongs in the stylesheet, not in a
    // measurement racing an image decode.

    element.querySelectorAll("[data-install-dismiss]").asList().forEach { control ->
        control.onClick {
            recordDismissed()
            element.removeAttribute("data-install-visible")
        }
    }

    reportTapsFrom(element, surface = "bar")

    // The shown side of the funnel. Without it a count of store taps says only that some people
    // tap, not whether the offer works.
    Analytics.track(
        AnalyticsEvent.INTERSTITIAL_SHOWN,
        AnalyticsParam.PLATFORM to platform,
        AnalyticsParam.SURFACE to "bar"
    )
}

/**
 * Report a tap on any store link inside [element].
 *
 * The link already carries the campaign tags that let the install itself be joined back here; this
 * counts the taps, which is the half of the funnel no store reports back. A tap is also treated as
 * a dismissal: somebody who has been sent to a store has answered the question, and asking again
 * on the page they return to is noise.
 */
private fun reportTapsFrom(element: Element, surface: String) {
    element.querySelectorAll("a[data-install-platform]").asList().forEach { link ->
        val platform = (link as? Element)?.getAttribute("data-install-platform") ?: return@forEach
        link.onClick {
            recordDismissed()
            Analytics.track(
                AnalyticsEvent.INTERSTITIAL_TAPPED,
                AnalyticsParam.PLATFORM to platform,
                AnalyticsParam.SURFACE to surface
            )
        }
    }
}

/**
 * Which store this device should be offered on the bar, or null if it should see no bar at all.
 *
 * The rule itself is [installBarPlatform] in `web-common`, so that it can be unit-tested against
 * real user-agent strings on the JVM — it is the decision the whole design rests on, and every way
 * of getting it wrong is invisible from the outside. This is only the part that has to run in a
 * browser: reading the user agent.
 */
private fun barPlatform(): String? = installBarPlatform(window.navigator.userAgent)

/**
 * Whether the offer has been answered recently, either by dismissing it or by following it to a
 * store.
 *
 * An unreadable `localStorage` — Safari private browsing, or any browser with site data blocked —
 * is treated as "dismissed". A bar that cannot remember being dismissed would come back on every
 * single page, which is the behaviour this replaced.
 */
private fun isDismissed(): Boolean = try {
    val recorded = localStorage.getItem(DISMISSED_KEY)?.toDoubleOrNull()
    recorded != null && Date.now() - recorded < DISMISS_QUIET_PERIOD_MS
} catch (t: Throwable) {
    true
}

private fun recordDismissed() {
    try {
        localStorage.setItem(DISMISSED_KEY, Date.now().toString())
    } catch (t: Throwable) {
        // Nothing useful to do. The bar will be offered again, which is the harmless direction to
        // fail in for a control that is one tap to close.
        console.warn("The app install dismissal could not be stored")
    }
}

private const val DISMISSED_KEY = "vegasful_app_install_dismissed"

/**
 * How long an answer is honoured before the offer comes back. Matches the email capture overlay's
 * quiet period, for the same reason: a "no" now is not a "no" in two months.
 */
private const val DISMISS_QUIET_PERIOD_MS = 30.0 * 24 * 60 * 60 * 1000
